import { useState, useEffect, useCallback } from "react";
import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { useAuthWithTestMode } from "@/hooks/use-auth-with-test-mode";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, AlertCircle, Loader2, Link2, Unlink } from "lucide-react";
import { fetchWithAuth } from "@/lib/utils";

interface IdentityLink {
  id: string;
  platform: string;
  platformUserId: string;
  displayName?: string;
  linkedAt: string;
}

type ConnectState = "idle" | "linking" | "success" | "error" | "not_authenticated";

function platformLabel(platform: string, t: TFunction): string {
  switch (platform) {
    case "discord":
      return "Discord";
    case "slack":
      return "Slack";
    case "teams":
      return "Teams";
    default:
      return platform || t("identity.platform");
  }
}

/**
 * Identity Connect Page
 *
 * Shown when the user navigates to /identity/connect?token=...
 * The token is a bridge-minted JWT from the Discord, Slack, or Teams slash command.
 * This page calls POST /api/identity/link-token with the token to link
 * the platform identity to the authenticated WorkOS user.
 *
 * Also shows existing links and allows unlinking.
 */
export function IdentityConnectPage() {
  const { t } = useTranslation();
  const { user, isLoading: authLoading, getAccessToken } = useAuthWithTestMode();
  const [connectState, setConnectState] = useState<ConnectState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [linkedPlatform, setLinkedPlatform] = useState("");
  const [links, setLinks] = useState<IdentityLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(true);

  const token = new URLSearchParams(window.location.search).get("token");

  const loadLinks = useCallback(async () => {
    try {
      const res = await fetchWithAuth("/api/identity/links", {} as RequestInit, getAccessToken);
      if (res.ok) {
        const data = await res.json();
        setLinks(data.links ?? []);
      }
    } catch {
      // ignore — links are best-effort
    } finally {
      setLinksLoading(false);
    }
  }, [getAccessToken]);

  useRefreshOnReconnect(loadLinks, !authLoading && Boolean(user));

  // Load existing links on mount
  useEffect(() => {
    if (!authLoading && user) {
      loadLinks();
    } else if (!authLoading && !user) {
      setLinksLoading(false);
    }
  }, [authLoading, user, loadLinks]);

  // Auto-link when token is present and user is authenticated
  useEffect(() => {
    if (!token || authLoading || connectState !== "idle") return;
    if (!user) {
      setConnectState("not_authenticated");
      return;
    }

    (async () => {
      setConnectState("linking");
      try {
        const res = await fetchWithAuth(
          "/api/identity/link-token",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          },
          getAccessToken,
        );

        const data = await res.json();

        if (res.ok) {
          setConnectState("success");
          setLinkedPlatform(data.link?.platform ?? t("identity.unknownPlatform"));
          // Refresh links
          loadLinks();
        } else {
          setConnectState("error");
          setErrorMessage(data.error ?? t("identity.linkFailed"));
        }
      } catch (err: any) {
        setConnectState("error");
        setErrorMessage(err.message ?? t("identity.networkError"));
      }
    })();
  }, [token, authLoading, user, connectState, getAccessToken, loadLinks, t]);

  const handleUnlink = async (platform: string, platformUserId: string) => {
    try {
      const res = await fetchWithAuth(
        "/api/identity/link",
        {
          method: "DELETE",
          body: JSON.stringify({ platform, platformUserId }),
        } as RequestInit,
        getAccessToken,
      );
      if (res.ok) {
        loadLinks();
      }
    } catch {
      // ignore
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Not authenticated ──────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground">
        <div className="w-full max-w-md space-y-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/60">
            <AlertCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">{t("identity.signInRequired")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {token ? t("identity.signInToLinkPlatform") : t("identity.signInToLinkAccount")}
            </p>
          </div>
          <Button
            onClick={() => {
              // Redirect to the main page which has the sign-in flow
              window.location.href = "/?return_to=" + encodeURIComponent(window.location.pathname + window.location.search);
            }}
            className="w-full"
          >
            {t("identity.signIn")}
          </Button>
        </div>
      </div>
    );
  }

  // ── Token linking in progress ──────────────────────────────────────────
  const showTokenResult = token && (connectState === "linking" || connectState === "success" || connectState === "error");

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground">
      <div className="w-full max-w-md space-y-6">
        {/* Token linking result */}
        {showTokenResult && (
          <Card className="p-6">
            {connectState === "linking" && (
              <div className="flex flex-col items-center space-y-3 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500 dark:text-blue-400" />
                <p className="text-sm text-muted-foreground">{t("identity.linking")}</p>
              </div>
            )}
            {connectState === "success" && (
              <div className="flex flex-col items-center space-y-3 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/60">
                  <CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-foreground">
                    {t("identity.connected", { platform: platformLabel(linkedPlatform, t) })}
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("identity.connectedDescription", { platform: platformLabel(linkedPlatform, t) })}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => { window.location.href = "/"; }}
                  className="mt-2"
                >
                  {t("common:actions.backToChat")}
                </Button>
              </div>
            )}
            {connectState === "error" && (
              <div className="flex flex-col items-center space-y-3 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/60">
                  <AlertCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-foreground">{t("identity.linkingFailed")}</h1>
                  <p className="mt-1 text-sm text-muted-foreground">{errorMessage}</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => { window.location.href = "/"; }}
                  className="mt-2"
                >
                  {t("common:actions.backToChat")}
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Existing connections */}
        <Card className="p-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Link2 className="h-4 w-4" />
            {t("identity.connectedChannels")}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("identity.connectedChannelsDescription")}
          </p>

          {linksLoading ? (
            <div className="mt-4 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : links.length === 0 ? (
            <div className="mt-4 rounded-md bg-muted/60 p-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t("identity.noChannels")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                <Trans
                  i18nKey="identity.connectHint"
                  components={{ command: <code className="rounded bg-muted px-1 text-foreground" /> }}
                />
              </p>
            </div>
          ) : (
            <ul className="mt-4 space-y-2">
              {links.map((link) => (
                <li
                  key={link.id}
                  className="flex items-center justify-between rounded-md border bg-muted/60 px-3 py-2"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold uppercase text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300">
                      {link.platform.slice(0, 2)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {platformLabel(link.platform, t)}
                      </p>
                      {link.displayName && (
                        <p className="text-xs text-muted-foreground">{link.displayName}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUnlink(link.platform, link.platformUserId)}
                    className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                    aria-label={t("identity.unlink", { platform: platformLabel(link.platform, t) })}
                  >
                    <Unlink className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Back link */}
        <div className="text-center">
          <button
            onClick={() => { window.location.href = "/"; }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← {t("common:actions.backToChat")}
          </button>
        </div>
      </div>
    </div>
  );
}
